// src/components/resources/tools/generators/AiChat/AiChatPreview.tsx

import type { JSX } from 'react'
import { cn } from '~/lib/utils'
import { useAiChatState } from './AiChatContext'
import { ChatGptThread } from './ChatGptThread'
import { ClaudeThread } from './ClaudeThread'
import { GeminiThread } from './GeminiThread'

/**
 * Renders the active provider's conversation surface inside a fixed-height,
 * device-like frame. The frame is what html2canvas captures; the inner thread
 * scrolls, and the exporter's `expandScrollableContent` flag grows the capture
 * so long transcripts export in full instead of being clipped to the frame.
 *
 * Reads from the deferred state snapshot (via `DeferredAiChatStateProvider`),
 * so it carries no editable inputs — all editing happens in the controls column.
 */
export function AiChatPreview(): JSX.Element {
  const { settings, messages } = useAiChatState()

  // The frame border is part of the html2canvas capture (previewRef wraps it),
  // so it must key off the MOCKUP theme (`settings.theme`) — NOT a `dark:`
  // variant, which would resolve against the visitor's SITE theme and export
  // the same mockup with a different border depending on light/dark site mode.
  const dark = settings.theme === 'dark'

  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border shadow-2xl ring-1 ring-black/5',
        dark ? 'border-slate-700' : 'border-slate-200',
      )}
    >
      <div className="h-[760px] w-full">
        {settings.provider === 'chatgpt' ? <ChatGptThread settings={settings} messages={messages} /> : null}
        {settings.provider === 'claude' ? <ClaudeThread settings={settings} messages={messages} /> : null}
        {settings.provider === 'gemini' ? <GeminiThread settings={settings} messages={messages} /> : null}
      </div>
    </div>
  )
}
